import React from 'react';
import Button from '@material-ui/core/Button';
import type { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/styles';
import { ReactComponent as CopyIcon } from '../../../images/copy.svg';
import { ReactComponent as DeleteIcon } from '../../../images/delete.svg';
import { ReactComponent as DisabledDeleteIcon } from '../../../images/disabledDelete.svg';
import { ReactComponent as DisabledPasteIcon } from '../../../images/disabledPaste.svg';
import { ReactComponent as PasteIcon } from '../../../images/paste.svg';
import { BLOCK_TYPE_KEYS } from '../../../lib/constants';
import { useShallowSelector, useDispatch } from '../../../lib/reduxHooks';
import { pasteCacheBlocks } from '../../../lib/utils';
import type { IRootState } from '../../../store';

import * as actions from '../../../store/senderBuild/actions';

const useStyles = makeStyles((theme: Theme) => ({
  actions: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    marginLeft: -1 * theme.spacing(1),
  },
  actionIcon: {
    height: 18,
    width: 18,
    marginRight: theme.spacing(1),
  },
  actionSection: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(1),
  },
  toolbarActionSection: {
    padding: theme.spacing(1),
  },
}));

interface BlockActionsProps {
  isPasteButtonDisabled: boolean;
  isBlockToolbar?: boolean;
  blockType?: string;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const blockActionsSelector = (state: IRootState) => {
  const {
    currentPage,
    currentDocument,
    blocks,
    copyCache,
    selectedBlocks,
    pageDimensions,
    id,
    groups,
    isDeletingBlocks,
    isCreatingBlock,
  } = state.senderBuild;
  return {
    currentPage,
    currentDocument,
    blocks,
    copyCache,
    selectedBlocks,
    pageDimensions,
    id,
    groups,
    isDeletingBlocks,
    isCreatingBlock,
  };
};

export function BlockActions(props: BlockActionsProps) {
  const { isPasteButtonDisabled, isBlockToolbar, blockType } = props;
  const {
    selectedBlocks,
    blocks,
    copyCache,
    currentPage,
    currentDocument,
    pageDimensions,
    groups,
    id,
    isDeletingBlocks,
    isCreatingBlock,
  } = useShallowSelector(blockActionsSelector);

  const dispatch = useDispatch();
  const classes = useStyles(props);

  const selectedPageBlocks = blocks[selectedBlocks.documentId!][selectedBlocks.pageIndex!];
  const isStrikeBlock = blockType === BLOCK_TYPE_KEYS.STRIKE;

  function copyBlocks() {
    const copiedBlocks = selectedBlocks.blockIndices.map((i) => selectedPageBlocks[i]);
    dispatch(actions.copyBlocksToCache(copiedBlocks));
  }

  function pasteBlocks() {
    const { pasteCounter } = copyCache;
    const pasteBlocksObj = pasteCacheBlocks({
      pasteCounter,
      copyCache,
      blocks,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      pageHeight: pageDimensions[currentDocument][currentPage].height,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      pageWidth: pageDimensions[currentDocument][currentPage].width,
      selectedBlocks,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      currentDocument,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      currentPage,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      envelopeId: id,
      blockGroups: groups,
    });
    if (pasteBlocksObj.updatedGroup!.newBlock!.length > 0) {
      dispatch(
        actions.addNewBlocksToGroup(pasteBlocksObj.updatedGroup!.currentGroup, pasteBlocksObj.updatedGroup!.newBlock)
      );
    } else {
      dispatch(
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        actions.pasteBlocks(currentDocument, currentPage, pasteBlocksObj.blocksWithNewIds, pasteBlocksObj.newGroups)
      );
    }
  }

  function deleteBlock() {
    const blockIds = selectedBlocks.blockIndices.map(
      (blockIndex) => blocks[selectedBlocks.documentId!][selectedBlocks.pageIndex!][blockIndex].blockId!
    );
    dispatch(actions.deleteBlocks(selectedBlocks.documentId!, selectedBlocks.pageIndex!, blockIds));
  }

  return (
    <section className={isBlockToolbar ? classes.toolbarActionSection : classes.actionSection}>
      {!isBlockToolbar && <Typography variant="overline">Block Actions:</Typography>}
      <div
        className={
          isBlockToolbar && isStrikeBlock
            ? 'flex justify-around py-2 px-0'
            : isBlockToolbar
              ? 'flex justify-around p-2'
              : classes.actions
        }
      >
        <Button variant="text" color="primary" onClick={copyBlocks} data-spec="copy" startIcon={<CopyIcon />}>
          Copy
        </Button>
        <Button
          disabled={isPasteButtonDisabled}
          variant="text"
          color="primary"
          className={isBlockToolbar && isStrikeBlock ? 'ml-1' : 'ml-4'}
          data-spec="paste"
          onClick={pasteBlocks}
          startIcon={isPasteButtonDisabled ? <DisabledPasteIcon /> : <PasteIcon />}
        >
          Paste
        </Button>
        <Button
          disabled={isDeletingBlocks || isCreatingBlock}
          variant="text"
          color="primary"
          className={isBlockToolbar && isStrikeBlock ? 'ml-1' : 'ml-4'}
          onClick={deleteBlock}
          data-spec="delete"
          startIcon={isDeletingBlocks || isCreatingBlock ? <DisabledDeleteIcon /> : <DeleteIcon />}
        >
          Delete
        </Button>
      </div>
    </section>
  );
}
